import { Landmark, Repeat, FileText, Mail } from "lucide-react";
import { useLang } from "@/i18n";
import { OFFICIAL } from "@/data/media";
import { CopyButton } from "@/components/CopyButton";
import { FadeUp } from "@/components/motion";
import { L } from "@/components/L";

const Row = ({ label, children }) => (
  <div className="py-3 border-b border-[var(--line)] last:border-0">
    <p className="text-xs uppercase tracking-[0.18em] text-[var(--ink-soft)]">{label}</p>
    <div className="mt-1 text-[var(--ink)]">{children}</div>
  </div>
);

const Card = ({ icon: Icon, title, tone, children, delay, testId, corner }) => (
  <FadeUp delay={delay} className={`relative flex flex-col p-7 sm:p-8 ${corner} ${tone}`} data-testid={testId}>
    <span className="h-12 w-12 grid place-items-center rounded-full bg-white/80 text-[var(--green-deep)]"><Icon size={20} /></span>
    <h3 className="mt-6 text-2xl sm:text-3xl leading-tight">{title}</h3>
    <div className="mt-5 flex-1 flex flex-col">{children}</div>
  </FadeUp>
);

export const DonateCards = ({ full = false }) => {
  const { t } = useLang();
  const d = t.donate;
  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <Card icon={Landmark} title={d.bankTitle} tone="bg-white shadow-[0_0_0_1px_var(--line)]" corner="rounded-[2rem_2rem_2rem_0.4rem]" delay={0} testId="donate-card-bank">
        <Row label={d.ibanLabel}><p className="font-semibold tracking-wide text-lg break-all" data-testid="donate-iban">{OFFICIAL.ibanDisplay}</p></Row>
        <Row label={d.bankLabel}>{OFFICIAL.bank}</Row>
        <Row label={d.holderLabel}>{OFFICIAL.holder} · {OFFICIAL.address}</Row>
        {full && <Row label={d.causalLabel}><span className="text-[var(--ink-soft)]">{d.causal}</span></Row>}
        <div className="mt-5"><CopyButton value={OFFICIAL.iban} label={d.copyIban} testId="copy-iban-btn" /></div>
      </Card>
      <Card icon={Repeat} title={d.sddTitle} tone="bg-[var(--blue-pale)]" corner="rounded-[2rem_2rem_0.4rem_2rem]" delay={0.1} testId="donate-card-sdd">
        <p className="text-[var(--ink-soft)] leading-relaxed">{full ? d.sddText : d.sddText.split(". ")[0] + "."}</p>
        {full && <p className="mt-3 text-[var(--ink-soft)] leading-relaxed">{d.sddThanks}</p>}
        <p className="mt-4 font-medium flex gap-2"><FileText size={18} className="shrink-0 mt-0.5 text-[var(--green-deep)]" />{d.sddHow}</p>
        <div className="mt-auto pt-6 flex flex-wrap gap-2">
          <L to="contact" search={`?subject=${encodeURIComponent(d.ctaSdd)}`} className="btn btn-primary !py-2.5 !px-5 text-sm" data-testid="request-sdd-btn">{d.ctaSdd}</L>
          {full && <a href={`mailto:${OFFICIAL.email}?subject=${encodeURIComponent("SDD — MADE")}`} className="btn btn-ghost !py-2.5 !px-5 text-sm" data-testid="sdd-email-btn"><Mail size={14} /> Email</a>}
        </div>
        {full && <p className="mt-4 text-sm text-[var(--ink-soft)]">{t.contact.faxLabel} {OFFICIAL.fax} · {OFFICIAL.email}</p>}
      </Card>
      <Card icon={FileText} title={d.fiveTitle} tone="bg-[var(--yellow-pale)]" corner="rounded-[0.4rem_2rem_2rem_2rem]" delay={0.2} testId="donate-card-5x1000">
        <p className="text-[var(--ink-soft)] leading-relaxed">{d.fiveText}</p>
        <p className="mt-4 text-xs uppercase tracking-[0.18em] text-[var(--ink-soft)]">{d.cfLabel}</p>
        <p className="font-display text-4xl sm:text-5xl tracking-tight mt-1" data-testid="donate-cf">{OFFICIAL.cf}</p>
        <div className="mt-auto pt-6"><CopyButton value={OFFICIAL.cf} label={d.copyCf} testId="copy-cf-btn" /></div>
      </Card>
    </div>
  );
};
